<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  FhButton,
  FhCard,
  FhIcon,
  FhText,
  FhTitle,
} from '@fareharbor-com/beacon-vue';
import { help, messageExclamation } from '@fareharbor-com/beacon-vue/icons';
import { cards } from '@/common/constants/regionsHomePage';
import RegionCard from '@/components/RegionCard.vue';
import LocationSearchBar from '@/components/SearchBar/LocationSearchBar.vue';
import { useLocationSearch } from '@/composables/useLocationSearch';
import { useUserRoles } from '@/composables/useUserRoles';
import { ROUTES } from '@/router/routes';
import { useUserStore } from '@/stores/user';

const router = useRouter();
const userStore = useUserStore();
const { navigateToItemList, handleSubmitGoogleLocationSearch } =
  useLocationSearch();

const handleLocationSearch = (searchData: {
  text: string;
  coordinates: null | {
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  };
}) => {
  navigateToItemList(searchData);
};

// Create a wrapper function that calls handleSubmitGoogleLocationSearch and then navigates
const handleGoogleLocationSearch = (searchData: {
  text: string;
  coordinates: {
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  };
}) => {
  // First update the store data
  handleSubmitGoogleLocationSearch(searchData);
  // Then navigate to the item list with the same data
  navigateToItemList(searchData);
};

const { inFhdn, inFhdnApi } = useUserRoles(userStore);

onMounted(() => {
  userStore.fetchUserData();
});

// FHDN Links users see everything (How It Works, About, Footer sections)
const isFhdnLinksUser = computed(() => inFhdn.value && !inFhdnApi.value);

const handleContactSupport = () => {
  // Add contact support logic here
  window.open('mailto:strategicpartnerships@fareharbor.com', '_blank');
};
</script>

<template>
  <div
    :class="[
      'bg-gradient-to-b from-gray-50 to-white',
      isFhdnLinksUser ? 'min-h-screen' : '',
    ]"
  >
    <!-- Header Section -->
    <section class="relative bg-white">
      <div class="mx-auto text-center p-6">
        <FhTitle variant="display-3xl">
          FareHarbor Marketplace
          <template #subtitle>
            Explore our Network of 165,000+ Tours & Activities
          </template>
        </FhTitle>

        <div class="mt-10 max-w-2xl mx-auto">
          <LocationSearchBar
            class="w-full"
            :placeholder="'Search by location (city, state, region, etc.)'"
            @submitSearch="handleLocationSearch"
            @submitGoogleSearch="handleGoogleLocationSearch"
          />
        </div>
      </div>
    </section>

    <!-- Region Cards Grid -->
    <section class="px-6 py-8 pb-16">
      <div
        class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
      >
        <div v-for="card in cards" :key="card.region">
          <RegionCard
            :imageUrl="card.imagepath"
            :regionName="card.region"
            :searchParams="card.searchQuery"
            :neCoordinates="card.ne"
            :swCoordinates="card.sw"
          />
        </div>
      </div>
    </section>

    <!-- How It Works Section - Only for FHDN (not API) -->
    <section v-if="isFhdnLinksUser" class="bg-[#E7F0FA] py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FhTitle
          tag="h2"
          variant="display-2xl"
          class="text-center mb-12"
          :color="'primary-default'"
        >
          HOW IT WORKS
        </FhTitle>

        <div class="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- Step 1 -->
          <FhCard
            tag="div"
            size="lg"
            :elevated="true"
            class="text-center p-8 bg-white hover:shadow-xl transition-all duration-300"
          >
            <!-- Step Number -->
            <div
              class="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg shadow-lg mx-auto mb-2"
            >
              1
            </div>
            <FhTitle
              tag="h3"
              variant="display-md"
              class="mb-4"
              :color="'default'"
            >
              Discover Products
            </FhTitle>
            <FhText
              variant="body-md"
              :color="'default'"
              class="text-gray-600 leading-relaxed"
            >
              Browse thousands of tours and activities from our network of
              trusted providers.
            </FhText>
          </FhCard>

          <!-- Step 2 -->
          <FhCard
            tag="div"
            size="lg"
            :elevated="true"
            class="text-center p-8 bg-white hover:shadow-xl transition-all duration-300"
          >
            <!-- Step Number -->
            <div
              class="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg shadow-lg mx-auto mb-2"
            >
              2
            </div>
            <FhTitle
              tag="h3"
              variant="display-md"
              class="mb-4"
              :color="'default'"
            >
              Add Referral Links
            </FhTitle>
            <FhText
              variant="body-md"
              :color="'default'"
              class="text-gray-600 leading-relaxed"
            >
              Integrate booking links seamlessly into your website or share them
              directly with customers.
            </FhText>
          </FhCard>

          <!-- Step 3 -->
          <FhCard
            tag="div"
            size="lg"
            :elevated="true"
            class="text-center p-8 bg-white hover:shadow-xl transition-all duration-300"
          >
            <!-- Step Number -->
            <div
              class="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg shadow-lg mx-auto mb-2"
            >
              3
            </div>
            <FhTitle
              tag="h3"
              variant="display-md"
              class="mb-4"
              :color="'default'"
            >
              Earn 15% Commission
            </FhTitle>
            <FhText
              variant="body-md"
              :color="'default'"
              class="text-gray-600 leading-relaxed"
            >
              Receive competitive commissions on every confirmed booking
              generated through your referrals.
            </FhText>
          </FhCard>
        </div>
      </div>
    </section>

    <!-- Enhanced About Section - Only for FHDN (not API) -->
    <section v-if="isFhdnLinksUser" class="bg-white py-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <!-- Left Column - Text Content -->
          <div class="space-y-6">
            <FhTitle
              tag="h2"
              variant="display-2xl"
              :color="'primary-default'"
              class="mb-6"
            >
              About FareHarbor Marketplace
            </FhTitle>

            <FhText
              variant="body-lg"
              :color="'default'"
              class="text-gray-700 leading-relaxed"
            >
              The FareHarbor Marketplace is an exclusive tool for FareHarbor
              Distribution Network (FHDN) partners, designed to simplify the
              discovery, management, and resale of tours and activities.
            </FhText>

            <FhText
              variant="body-lg"
              :color="'default'"
              class="text-gray-700 leading-relaxed"
            >
              As an affiliate of the FHDN, earn a competitive 15% commission on
              sales.
            </FhText>

            <FhText
              variant="body-lg"
              :color="'default'"
              class="text-gray-700 leading-relaxed"
            >
              FareHarbor facilitates the connections and transfers all
              commissions, ensuring a seamless, efficient experience for
              everyone involved.
            </FhText>
          </div>

          <!-- Right Column - Visual -->
          <div class="flex justify-end">
            <div class="relative max-w-lg">
              <img
                src="/FHDN-Header-Jan-2024.webp"
                alt="FareHarbor Marketplace - Join the FHDN as an affiliate"
                class="w-full h-auto rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Section - Only for FHDN (not API) -->
    <footer v-if="isFhdnLinksUser" class="bg-[#E7F0FA] mt-16">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="text-center space-y-8">
          <div class="space-y-4">
            <FhTitle tag="h2" variant="display-xl" :color="'primary-default'">
              Need Help Getting Started?
            </FhTitle>
            <FhText
              variant="body-lg"
              :color="'default'"
              class="max-w-2xl mx-auto text-gray-600"
            >
              Get answers to common questions or reach out to our support team
              for personalized assistance.
            </FhText>
          </div>

          <div
            class="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <FhButton
              variant="primary"
              size="lg"
              class="min-w-[160px]"
              @click="router.push({ name: ROUTES.FAQ })"
            >
              <FhIcon :icon="help" class="mr-2" />
              Help Page
            </FhButton>

            <FhButton
              variant="secondary"
              size="lg"
              class="min-w-[160px]"
              @click="handleContactSupport"
            >
              <FhIcon :icon="messageExclamation" class="mr-2" />
              Contact Support
            </FhButton>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
/* Override LocationSearchBar width */
:deep(.w-\[26rem\]) {
  width: 100% !important;
}
</style>
